import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useNavigateWithOrigin } from '../../app/nav/originNav'
import { useTranslation } from 'react-i18next'
import Button from '../../components/ui/Button'
import { useTicketRequests } from './hooks/useTicketRequests'
import { useTicketSummary } from './hooks/useTicketSummary'
import { useTicketLookups } from './hooks/useTicketLookups'
import { useApproveReject } from './hooks/useApproveReject'
import PageHeader from '../../components/ui/PageHeader'
import RefreshButton from '../../components/ui/RefreshButton'
import KpiCards, { type KpiCard } from '../../components/ui/KpiCards'
import TicketFilters, { type TicketFilterValue } from './components/TicketFilters'
import TicketIdentifierSearch from './components/TicketIdentifierSearch'
import TicketTable from './components/TicketTable'
import { searchTerm } from '../../lib/searchTerm'
import { appStorageKey } from '../../lib/appStorage'
import RejectReasonDialog from './components/RejectReasonDialog'
import ConfirmCard from './components/ConfirmCard'
import { useToast } from '../../components/ui/ToastProvider'
import { exportTicketRequests } from './api/ticketRequestApi'
import { getApiErrorMessage } from '../../api/apiError'
import { useGrants } from '../../auth/useCurrentUser'
import { GRANTS } from '../../auth/grants'
import { TICKET_REQUEST_STATUS, type TicketRequestListItem } from './types'
import './tickets.css'

const DEFAULT_PAGE_SIZE = 50

const EMPTY_FILTERS: TicketFilterValue = { statusId: null, officeId: null, typeId: null, q: '' }

/** URL sorgu parametresini sayı-veya-null'a çevirir (boş/geçersiz → null). */
function numOrNull(value: string | null): number | null {
  if (value == null || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

// Görünüm durumu kalıcılığı (016): oturum-içi depolama (sessionStorage). Serileştirme için hafif
// bir sorgu-dizesi biçimi kullanılır (yalnız depolamada; URL'e yazılmaz).
// Anahtar uygulama ön eklidir (062 research D7a) — kullanıcı/persona geçişinde ön ek taramasıyla
// otomatik temizlenir; elle tutulan bir anahtar listesi yoktur.
const VIEW_STORAGE_KEY = appStorageKey('tk-list-view')

/** Kayıtlı sorgu-dizesini görünüm durumuna (filtre + sayfa + boyut) çevirir. */
function parseView(sp: URLSearchParams): {
  filters: TicketFilterValue
  page: number
  pageSize: number
} {
  return {
    filters: {
      statusId: numOrNull(sp.get('status')),
      officeId: numOrNull(sp.get('office')),
      typeId: numOrNull(sp.get('type')),
      // Arama metni kalıcı DEĞİL (kullanıcı kuralı 2026-08-31): sayfadan çıkıp dönünce sıfırlanır.
      // 016'nın filtre kalıcılığı DURUYOR — kural yalnız metni kapsar (durum/ofis/tür/sayfa kalıcı).
      q: '',
    },
    page: Math.max(0, numOrNull(sp.get('page')) ?? 0),
    // Bozuk/uyumsuz kayıtta (ör. size=0) geçerli pozitif değilse varsayılana düş (boş sayfa isteği önlenir).
    pageSize: ((s) => (s != null && s > 0 ? s : DEFAULT_PAGE_SIZE))(numOrNull(sp.get('size'))),
  }
}

/** Başlangıç görünümü: sessionStorage'daki son görünüm (F5 / menüden dönüş); yoksa boş (ilk giriş). */
function initialView() {
  let stored: string
  try {
    stored = sessionStorage.getItem(VIEW_STORAGE_KEY) ?? ''
  } catch {
    stored = ''
  }
  return parseView(new URLSearchParams(stored))
}

/**
 * Bilet Talep Takip → Talep Listesi ekranı (mockup ile birebir). KPI (kapsam-geneli) + filtre/arama
 * + sayfalı tablo. Rol bu aşamada sabit admin (backend kapsam kancası null → tümü).
 */
export default function TicketRequestListPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  // Talep Detay'a giderken bu listenin konumu (path + URL query'si) origin taşınır → detayda "Geri" buraya
  // döner. Filtre/görünüm kalıcılığını origin DEĞİL, aşağıdaki sessionStorage (016) sağlar — ayrı mekanizma.
  const goDetail = useNavigateWithOrigin()
  // Başlangıç görünümü mount'ta bir kez hesaplanır (sessionStorage'daki son görünüm, yoksa boş).
  const initialRef = useRef<ReturnType<typeof initialView> | null>(null)
  if (initialRef.current === null) initialRef.current = initialView()
  const initial = initialRef.current

  // Görünüm durumu (filtre/arama/sayfa/boyut) sessionStorage'da tutulur: F5 yenileme ve başka ekrana
  // gidip geri dönmede (menüden dönüş dahil) korunur — 016 FR-001..006.
  const [filters, setFilters] = useState<TicketFilterValue>(initial.filters)
  const [page, setPage] = useState(initial.page)
  const [pageSize, setPageSize] = useState(initial.pageSize)
  const [debouncedQ, setDebouncedQ] = useState(initial.filters.q)
  const [exporting, setExporting] = useState(false)
  // Özel kalem (037) tam-kimlik araması (uygulanmış terim): boşken yalnız kendi kayıtları; tam PNR veya tam
  // Ad-Soyad ile başka personelin talepleri de gelir. Terim backend'e hem PNR hem Ad-Soyad tam-eşleşmesi
  // olarak denenir (OR). sessionStorage'a yazılmaz (geçici, kişisel-veri araması).
  const [exact, setExact] = useState('')

  // Arama kutusu her tuşta istek atmasın: q'yu 300ms debounce et (filtreler anında uygulanır).
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(filters.q), 300)
    return () => clearTimeout(id)
  }, [filters.q])

  // State → sessionStorage senkronu: filtre/sayfa/boyut değişince yalnız varsayılandan sapan alanlar
  // hafif bir sorgu-dizesi olarak kaydedilir; sonraki girişte (F5 / menüden dönüş) geri yüklenir.
  useEffect(() => {
    const p = new URLSearchParams()
    if (filters.statusId != null) p.set('status', String(filters.statusId))
    if (filters.officeId != null) p.set('office', String(filters.officeId))
    if (filters.typeId != null) p.set('type', String(filters.typeId))
    // `q` BİLEREK yazılmaz — arama metni kalıcı değildir (bkz. parseView).
    if (page > 0) p.set('page', String(page))
    if (pageSize !== DEFAULT_PAGE_SIZE) p.set('size', String(pageSize))
    try {
      sessionStorage.setItem(VIEW_STORAGE_KEY, p.toString())
    } catch {
      /* sessionStorage kullanılamıyorsa kalıcılık atlanır */
    }
  }, [filters, page, pageSize])

  const query = useMemo(
    () => ({
      statusId: filters.statusId,
      officeId: filters.officeId,
      typeId: filters.typeId,
      q: searchTerm(debouncedQ), // ≥2 karakterde arama; altında tüm liste (021)
      exactPnr: exact || undefined,
      exactName: exact || undefined,
      page,
      size: pageSize,
    }),
    [filters.statusId, filters.officeId, filters.typeId, debouncedQ, exact, page, pageSize],
  )
  const list = useTicketRequests(query)
  const summary = useTicketSummary()
  const lookups = useTicketLookups()
  const { approve, reject } = useApproveReject()
  const toast = useToast()
  // Excel butonu yalnız TICKET_REQUEST_EXPORT yetkisi olan kullanıcıya görünür (FR-016; asıl koruma backend'de).
  const { hasGrant } = useGrants()
  const [rejectTarget, setRejectTarget] = useState<TicketRequestListItem | null>(null)
  // Onay popup'ı: tekil onay (014'te çoklu seçim/toplu onay kaldırıldı) — Figma kart (ConfirmCard).
  const [approveTarget, setApproveTarget] = useState<TicketRequestListItem | null>(null)

  const patchFilters = (patch: Partial<TicketFilterValue>) => {
    setFilters((prev) => ({ ...prev, ...patch }))
    setPage(0)
  }

  // Tüm filtre + aramayı tek adımda sıfırla (arama solundaki "Filtreleri Temizle"). debouncedQ'yu da
  // hemen boşalt ki 300 ms gecikme olmadan liste filtresiz yüklensin.
  const clearFilters = () => {
    setFilters(EMPTY_FILTERS)
    setDebouncedQ('')
    setExact('')
    setPage(0)
  }

  // 037: özel kalem tam-kimlik araması uygulanınca (Ara) liste yeni kapsamla yüklenir; sayfa başa döner.
  const applyExact = (term: string) => {
    setExact(term)
    setPage(0)
  }
  const canIdentifierSearch = hasGrant(GRANTS.TICKET_REQUEST_VIEW_OWN_OR_IDENTIFIER)
  // Detay navigasyonunda tam-kimlik kanıtını taşı (kendi-dışı talebin detayı yalnız bununla açılır).
  // Terim hem PNR hem Ad-Soyad olarak taşınır (backend OR ile hangisi uyarsa onunla doğrular).
  const reviewHref = (id: number) => {
    const sp = new URLSearchParams({ id: String(id) })
    if (exact) {
      sp.set('exactPnr', exact)
      sp.set('exactName', exact)
    }
    return `/tickets/detail?${sp.toString()}`
  }

  const notifyError = () =>
    toast.show({ severity: 'error', summary: t('tickets.actionError'), life: 4000 })

  // Excel dışa aktarma: aktif filtrelerle sunucudan .xlsx çeker (tüm filtreli kayıtlar) ve indirir.
  // Buton yalnız TICKET_REQUEST_EXPORT grant'i olan kullanıcıya görünür (hasGrant kontrolü aşağıda); asıl koruma
  // backend'de (@PreAuthorize TICKET_REQUEST_EXPORT + VIEW/VIEW_OWN).
  const onExport = async () => {
    setExporting(true)
    try {
      await exportTicketRequests(query)
    } catch (err) {
      // Varsa backend'in lokalize mesajı; yoksa ekranın genel hata toast'ı.
      const message = getApiErrorMessage(err)
      if (message) {
        toast.show({ severity: 'error', summary: message, life: 4000 })
      } else {
        notifyError()
      }
    } finally {
      setExporting(false)
    }
  }

  const doApprove = () => {
    const row = approveTarget
    if (!row) return
    approve.mutate(
      { id: row.id, body: { modDate: row.modDate } },
      {
        onSuccess: () => {
          toast.show({
            severity: 'success',
            summary: t('tickets.approve.success'),
            life: 3000,
          })
        },
        onError: notifyError,
        onSettled: () => setApproveTarget(null),
      },
    )
  }

  const onRejectConfirm = (rejectReasonId: number, rejectNote?: string) => {
    if (!rejectTarget) return
    reject.mutate(
      { id: rejectTarget.id, body: { rejectReasonId, rejectNote, modDate: rejectTarget.modDate } },
      {
        onSuccess: () => {
          toast.show({
            severity: 'success',
            summary: t('tickets.reject.success'),
            life: 3000,
          })
          setRejectTarget(null)
        },
        onError: notifyError,
      },
    )
  }

  const items = list.data?.content ?? []
  const totalElements = list.data?.page.totalElements ?? 0

  // Sekme sayıları (kapsam-geneli, filtreden bağımsız — KPI ile aynı kaynak).
  const counts = useMemo(() => {
    const s = summary.data
    const pending = s?.pending ?? 0
    const approved = s?.approved ?? 0
    const rejected = s?.rejected ?? 0
    return {
      all: pending + approved + rejected,
      byStatus: {
        [TICKET_REQUEST_STATUS.PENDING]: pending,
        [TICKET_REQUEST_STATUS.APPROVED]: approved,
        [TICKET_REQUEST_STATUS.REJECTED]: rejected,
      },
    }
  }, [summary.data])

  // KPI kartları (kapsam-geneli) — paylaşılan ui/KpiCards için jenerik model.
  const kpiCards: KpiCard[] = [
    { key: 'pending', label: t('tickets.kpi.pending'), value: String(summary.data?.pending ?? 0) },
    {
      key: 'approved',
      label: t('tickets.kpi.approved'),
      value: String(summary.data?.approved ?? 0),
    },
    {
      key: 'rejected',
      label: t('tickets.kpi.rejected'),
      value: String(summary.data?.rejected ?? 0),
    },
  ]

  return (
    <div className="page tickets-page">
      <ConfirmCard
        visible={approveTarget !== null}
        title={t('tickets.approve.title')}
        subtitle={approveTarget ? `${approveTarget.pnr} - ${approveTarget.nameSurname}` : undefined}
        message={approveTarget ? t('tickets.approve.confirm') : ''}
        confirmLabel={t('tickets.approve.accept')}
        tone="ok"
        submitting={approve.isPending}
        onConfirm={doApprove}
        onHide={() => setApproveTarget(null)}
      />
      <RejectReasonDialog
        visible={rejectTarget !== null}
        reasons={lookups.data?.rejectReasons ?? []}
        submitting={reject.isPending}
        subtitle={rejectTarget ? `${rejectTarget.pnr} - ${rejectTarget.nameSurname}` : undefined}
        onConfirm={onRejectConfirm}
        onHide={() => setRejectTarget(null)}
      />

      <div className="tk-panel">
        {/* Tek kart: banner + başlık + KPI + filtre + tablo (Figma "Bilet Talepleri"). */}
        <div className="tk-panel-top">
          <PageHeader
            title={t('tickets.title')}
            actions={
              <>
                {/* Yenile, dışa aktarmanın SOLUNDA (kullanıcı isteği 2026-08-27); önceden
                    filtre çubuğundaydı (`TicketFilters` → `FilterBar onRefresh`). */}
                <RefreshButton onRefresh={() => list.refetch()} />
                {/* Görünüm yanındaki "Yenile" ile AYNI (kullanıcı kararı 2026-08-27):
                    paylaşılan `.ui-toolbar-btn`. Bu sayfa paylaşılan `ExcelExportButton`'ı
                    kullanmadığı için o bileşendeki `outlined`→`text` değişikliğini almamıştı
                    ve iki komşu buton farklı görünüyordu. Dışa aktarma MANTIĞI taşınmadı
                    (kendi `onExport`/`exporting` durumu yerinde) — yalnız görünüm hizalandı. */}
                {hasGrant(GRANTS.TICKET_REQUEST_EXPORT) && (
                  <Button
                    icon="pi pi-file-excel"
                    label={t('tickets.export')}
                    text
                    className="ui-toolbar-btn"
                    loading={exporting}
                    onClick={onExport}
                  />
                )}
                {/* Kapı EKLENDİ (push kapısı review'ü 21.10.2026) — gerekçe `ActivityListPage`'in
                    aynısı: rota `TICKET_REQUEST_CREATE`'e kilitli, düğme hiçbir şey sormuyordu. */}
                {hasGrant(GRANTS.TICKET_REQUEST_CREATE) && (
                  <Button
                    icon="pi pi-plus"
                    label={t('tickets.newRequest')}
                    onClick={() => navigate('/tickets/new')}
                  />
                )}
              </>
            }
          />

          <KpiCards cards={kpiCards} loading={summary.isLoading} />
        </div>
        {/* Özel kalem personeli (037): tam PNR/Ad-Soyad araması — ana aramanın HEMEN SAĞINDA
            (kullanıcı isteği 2026-08-27). Önceden filtre çubuğuna sığmadığı gerekçesiyle ayrı bir
            satırdaydı; `FilterBar.searchExtra` slotu o satırı gereksiz kıldı. Yetki backend'de de
            zorunlu — buradaki koşul yalnız arayüz kolaylığı. */}
        <TicketFilters
          lookups={lookups.data}
          counts={counts}
          value={filters}
          onChange={patchFilters}
          onClear={clearFilters}
          searchExtra={
            canIdentifierSearch ? (
              <TicketIdentifierSearch
                value={exact}
                onSearch={applyExact}
                onClear={() => applyExact('')}
              />
            ) : undefined
          }
        />
        <TicketTable
          items={items}
          loading={list.isLoading}
          page={page}
          rows={pageSize}
          totalElements={totalElements}
          onPage={(p, r) => {
            setPage(p)
            setPageSize(r)
          }}
          onReview={(id) => goDetail(reviewHref(id))}
          onApprove={(row) => setApproveTarget(row)}
          onReject={(row) => setRejectTarget(row)}
          exactPnr={exact || undefined}
          exactName={exact || undefined}
        />
      </div>
    </div>
  )
}
